import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import type { Station } from '@/types';

interface StationActionsProps {
  station: Station;
  className?: string;
}

const GOV_UK_REPORT_URL = 'https://www.gov.uk/report-fuel-prices';

export function StationActions({ station, className }: StationActionsProps) {
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const handleShare = useCallback(async () => {
    // Generate shareable URL with station ID
    const shareUrl = new URL(window.location.origin);
    shareUrl.searchParams.set('station', station.id);

    const shareData = {
      title: `${station.name} - PumpScout`,
      text: `Check fuel prices at ${station.name}`,
      url: shareUrl.toString(),
    };

    try {
      // Try native share first (mobile)
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareUrl.toString());
        setShowCopiedToast(true);
        setTimeout(() => setShowCopiedToast(false), 2000);
      }
    } catch (err) {
      // User cancelled or error - try clipboard as fallback
      if ((err as Error).name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(shareUrl.toString());
          setShowCopiedToast(true);
          setTimeout(() => setShowCopiedToast(false), 2000);
        } catch {
          console.error('Failed to copy to clipboard');
        }
      }
    }
  }, [station.id, station.name]);

  const handleReport = useCallback(() => {
    window.open(GOV_UK_REPORT_URL, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <div className={clsx('relative', className)}>
      <div className="flex gap-3">
        {/* Share button */}
        <button
          type="button"
          onClick={handleShare}
          className={clsx(
            'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl',
            'bg-brand-50 text-brand-700 font-medium',
            'hover:bg-brand-100 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
            'min-h-[44px]' // Minimum touch target
          )}
          data-track="share_click"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
            />
          </svg>
          Share
        </button>

        {/* Report button */}
        <button
          type="button"
          onClick={handleReport}
          className={clsx(
            'flex items-center justify-center gap-2 px-4 py-3 rounded-xl',
            'bg-gray-100 text-gray-700',
            'hover:bg-gray-200 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
            'min-h-[44px]' // Minimum touch target
          )}
          title="Report incorrect price on GOV.UK"
          data-track="report_click"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="sr-only sm:not-sr-only">Report</span>
        </button>
      </div>

      {/* "Copied!" toast */}
      <AnimatePresence>
        {showCopiedToast && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute left-1/2 -translate-x-1/2 -top-12 z-10"
          >
            <div className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Copied!
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report link text */}
      <p className="mt-3 text-center text-xs text-gray-500">
        <a
          href={GOV_UK_REPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          Report incorrect price on GOV.UK →
        </a>
      </p>
    </div>
  );
}
